import { Component, ViewEncapsulation, ViewChild, OnDestroy } from '@angular/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { RouterOutlet } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { TranslationService } from '../../../shared/services/translation.service';
import { WizardStep } from '../../models/app-wizard-state';
import { AppWizardService } from '../../services/app-wizard.service';
import { UserService } from '../../../shared/services/user.service';
import { FormOffer } from '../../models/form-offer';
import { OfferHeaderContent } from '../../../shared/models/settings';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { LocationService } from '../../../shared/services/location.service';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';

import { WizardStepperComponent } from './wizard-stepper/wizard-stepper.component';
import { WizardPageContent } from './wizard-page-content';

@Component({
	selector: 'copo-offer-wizard',
	templateUrl: './wizard.component.html',
	styleUrl: './wizard.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		PageHeaderComponent,
		NgIf,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		WizardStepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective,
	],
})
export class WizardComponent implements OnDestroy {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;

	readonly steps = ['eoffer.wizard.terms', 'eoffer.wizard.specs', 'eoffer.wizard.cart', 'eoffer.wizard.end'];
	pageContent: WizardPageContent | undefined;
	isLoading = false;
	readonly hasUserSubmitterRole: boolean;
	readonly readOnlyUser?: boolean;

	pageTitle: string;
	pathHeader: string;
	previousPage: string | undefined;

	private readonly pageTitleTranslationCode = 'offers.create.request.offer';

	constructor(
		private readonly userService: UserService,
		private readonly appWizardService: AppWizardService,
		private readonly translationService: TranslationService,
		private readonly locationService: LocationService
	) {
		this.hasUserSubmitterRole = this.userService.user.isOfferSubmitter();
		this.readOnlyUser = this.userService.user.readOnly;
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		this.pathHeader = this.getPathHeader();
		this.previousPage = this.appWizardService.actionToGoBack();
		if (!this.previousPage) {
			this.previousPage = this.appWizardService.actionRetrievePreviousPage();
		}
	}

	ngOnDestroy() {
		this.appWizardService.actionCleanUrlData();
		this.appWizardService.actionSavePreviousPage(this.previousPage);
		this.appWizardService.cleanCart();
	}

	resetForm() {
		this.appWizardService.actionResetCurrentOffer();
	}

	isStepHeader() {
		return this.appWizardService.getState().stepIndex === WizardStep.HEADER;
	}

	isStepConfirmation() {
		return this.appWizardService.getState().stepIndex === WizardStep.REVIEW;
	}

	onActivate(pageContent: WizardPageContent) {
		this.pageContent = pageContent;
		if (!this.scrollBar) {
			return;
		}
		const scrollPosition = this.appWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex];
		this.scrollBar.scrollTo({ top: scrollPosition });
	}

	onDeactivate() {
		if (!this.pageContent || !this.scrollBar) {
			return;
		}
		this.appWizardService.getState().scrollPositionByStep[this.pageContent.stepIndex] =
			this.scrollBar.measureScrollOffset('top');
		this.pageContent = undefined;
	}

	getPathHeader(): string {
		switch (this.appWizardService.getState().stepIndex) {
		case WizardStep.HEADER:
			this.pathHeader = '';
			break;
		case WizardStep.CART:
			this.pathHeader = '';
			break;
		case WizardStep.QUANTITY:
			this.pathHeader = '';
			break;
		case WizardStep.REVIEW:
			this.pathHeader = '';
			break;
		default:
			break;
		}
		return this.pathHeader;
	}

	async goNextStep() {
		if (!this.pageContent || this.pageContent.checkError() || this.pageContent.checkErrorForDraft()) {
			return;
		}
		if (this.pageContent.stepIndex === WizardStep.HEADER) {
			try {
				await this.updateSettingOffer(this.appWizardService.getStateOfferForm());
			} catch (error) {
				console.error('error while updating the settings', error);
			}
		}
		await this.appWizardService.actionGoNextStep();
	}

	async evtSaveOffer() {
		if (!this.pageContent || this.pageContent.checkErrorForDraft()) {
			return;
		}
		this.isLoading = true;
		await this.appWizardService.actionSaveCurrentOffer();
		this.isLoading = false;
	}

	getCurrentStepIndex() {
		return this.appWizardService.getState().stepIndex;
	}

	getCurrentStatus(): boolean {
		return !this.appWizardService.getCurrentRoute().endsWith('wizard/stepReview');
	}

	goToBackView() {
		if (this.appWizardService.getCurrentRoute().endsWith('wizard/stepHeader')) {
			this.locationService.go(-1, this.previousPage);
		}
		if (this.appWizardService.getCurrentRoute().endsWith('wizard/stepCart')) {
			this.locationService.go(-2, this.previousPage);
		}
		if (this.appWizardService.getCurrentRoute().endsWith('wizard/stepQuantity')) {
			this.locationService.go(-3, this.previousPage);
		}
		if (this.appWizardService.getCurrentRoute().endsWith('wizard/stepReview')) {
			this.locationService.go(-4, this.previousPage);
		}
	}

	isButtonPreviousVisible() {
		return (
			!this.isLoading
		);
	}

	isButtonPreviousEnabled() {
		return !this.isLoading;
	}

	isButtonNextVisible() {
		return !this.isLoading && this.appWizardService.getState().stepIndex < WizardStep.REVIEW
			&& this.appWizardService.getState().stepIndex > WizardStep.HEADER;
	}

	isButtonViewCardVisible() {
		return !this.isLoading && this.appWizardService.getState().stepIndex === WizardStep.HEADER;
	}

	isButtonNextEnabled() {
		return !this.isLoading;
	}

	isButtonSaveVisible() {
		return (
			!this.isLoading
			&& !this.appWizardService.getState().errorInPage
			&& this.appWizardService.getState().stepIndex !== WizardStep.REVIEW
		);
	}

	isButtonSaveEnabled() {
		return this.hasUserSubmitterRole;
	}

	isButtonSubmitVisible() {
		return !this.isLoading && this.appWizardService.getState().stepIndex === WizardStep.REVIEW;
	}

	isButtonSubmitEnabled() {
		return this.hasUserSubmitterRole;
	}

	getProductCount() {
		return this.appWizardService.getStateOfferForm().getFormProducts().length;
	}

	private async updateSettingOffer(formOffer: FormOffer) {
		const offerHeader = {
			soldTo: <string> formOffer.getFormSoldTo().value,
			shipTo: <string> formOffer.getFormShipTo().value,
			endUser: <string> formOffer.getFormEndUser().value,
			idd: <string> formOffer.getFormIdd().value,
			tmode: <string> formOffer.getFormTmode().value,
			incot: <string> formOffer.getFormIncoterm().value,
			pricingUnit: <string> formOffer.getFormPricingUnit().value,
			currency: <string> formOffer.getFormCurrency().value,
		};
		const offerHeaderSettings = this.userService.user.getSettings('offer_header');
		if (
			offerHeaderSettings
			&& this.isSameSettingOffer(offerHeader, offerHeaderSettings.value.offerHeader)
		) {
			return false;
		}
		if (offerHeaderSettings) {
			offerHeaderSettings.value = {
				version: 1,
				offerHeader,
			};
		} else {
			this.userService.user.settings.push({
				key: 'offer_header',
				value: {
					version: 1,
					offerHeader,
				},
			});
		}
		await this.userService.updateSettings();
		return this.userService.updateUserData();
	}

	private isSameSettingOffer(offerHeader1: OfferHeaderContent, offerHeader2: OfferHeaderContent) {
		return JSON.stringify(offerHeader1) === JSON.stringify(offerHeader2);
	}
}
